import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react';

import type { Health } from '@/domain/types';

import { Spinner } from './icons';

export type PanelEdge = Health | 'none';

/* The 3px edge that says "this needs attention", in the status colour; table rows draw the same. */
const EDGE: Record<PanelEdge, string> = {
  ok: 'before:bg-status-ok',
  impaired: 'before:bg-status-impaired',
  degraded: 'before:bg-status-degraded',
  failed: 'before:bg-status-failed',
  maintenance: 'before:bg-status-maintenance',
  none: 'before:bg-status-none',
};

/**
 * The one card: an 8px-cornered, 1px-bordered surface. The header holds a 15px title with an
 * optional count and description, and a divider unless the panel wraps a form, where the title
 * sits as a section label. `edge` paints the attention stripe on the left.
 */
export function Panel({
  title,
  titleId,
  count,
  description,
  actions,
  children,
  className,
  bodyClassName,
  edge,
  divided = true,
  as: Tag = 'section',
  ...rest
}: Omit<HTMLAttributes<HTMLElement>, 'title' | 'children'> & {
  title?: ReactNode;
  titleId?: string;
  count?: number;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  edge?: PanelEdge | undefined;
  divided?: boolean;
  as?: 'section' | 'div' | 'article' | 'li';
}) {
  return (
    <Tag
      className={[
        'relative overflow-hidden rounded-lg border border-line bg-surface',
        edge ? `before:absolute before:inset-y-0 before:left-0 before:w-[3px] ${EDGE[edge]}` : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {title || actions ? (
        <header
          className={[
            'flex flex-wrap items-start justify-between gap-3 px-5 pt-4',
            divided ? 'pb-3 border-b border-line' : 'pb-1',
          ].join(' ')}
        >
          <div className="min-w-0">
            {title ? (
              <div className="flex items-baseline gap-2">
                <h2 id={titleId} className="text-title font-medium">
                  {title}
                </h2>
                {count !== undefined ? (
                  <span className="text-body text-ink-3 tabular">{count}</span>
                ) : null}
              </div>
            ) : null}
            {description ? <p className="text-body text-ink-2 mt-0.5">{description}</p> : null}
          </div>
          {actions ? <div className="flex items-center gap-2 shrink-0">{actions}</div> : null}
        </header>
      ) : null}
      <div className={bodyClassName ?? (divided ? 'p-5' : 'px-5 pb-5 pt-2')}>{children}</div>
    </Tag>
  );
}

export function EmptyState({
  icon,
  title,
  body,
  action,
  compact = false,
}: {
  icon?: ReactNode;
  title: string;
  body?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div
      className={[
        'flex flex-col items-center text-center animate-fade-in',
        compact ? 'py-6 px-4' : 'py-12 px-6',
      ].join(' ')}
    >
      {icon ? <div className="text-ink-3 mb-3">{icon}</div> : null}
      <p className="text-lead font-medium text-ink">{title}</p>
      {body ? <p className="mt-1 max-w-[52ch] text-body text-ink-2">{body}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

/** A spinner and a word. It stays invisible for the first 300ms so a quick answer never flashes it. */
export function Loading({ label = 'Loading…', className }: { label?: string; className?: string }) {
  return (
    <div
      className={[
        'flex items-center gap-2 text-body text-ink-3 py-8 justify-center animate-late',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role="status"
    >
      <Spinner size={16} />
      {label}
    </div>
  );
}

/** A placeholder the size of the text it stands in for, shown only while a first load is in flight. */
export function Skeleton({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={['skeleton inline-block h-[1em] w-24 align-middle', className]
        .filter(Boolean)
        .join(' ')}
    />
  );
}

export function ErrorNotice({
  title,
  message,
  action,
}: {
  title?: string;
  message: string;
  action?: ReactNode;
}) {
  return (
    <div
      role="alert"
      className="rounded-sm border border-danger/40 bg-danger-soft text-danger-ink px-3 py-2.5 text-body animate-fade-in"
    >
      {title ? <p className="font-medium">{title}</p> : null}
      <p className={title ? 'mt-0.5' : ''}>{message}</p>
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function Notice({
  tone = 'info',
  children,
  className,
}: {
  tone?: 'info' | 'warning' | 'danger' | 'success';
  children: ReactNode;
  className?: string;
}) {
  const cls = {
    info: 'border-brand/40 bg-brand-soft text-ink',
    warning: 'border-status-impaired/40 bg-status-impaired-soft text-ink',
    danger: 'border-danger/40 bg-danger-soft text-danger-ink',
    success: 'border-status-ok/40 bg-status-ok-soft text-ink',
  }[tone];
  return (
    <div
      className={['rounded-sm border px-3 py-2.5 text-body', cls, className]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  );
}

export function Kv({
  items,
  className,
}: {
  items: { label: string; value: ReactNode }[];
  className?: string;
}) {
  return (
    <dl
      className={[
        'grid grid-cols-1 gap-y-1 sm:grid-cols-[max-content_minmax(0,1fr)] sm:items-baseline sm:gap-x-6 sm:gap-y-2 text-body',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {items.map((item) => (
        <div key={item.label} className="contents">
          <dt className="text-meta text-ink-3">{item.label}</dt>
          <dd className="m-0 min-w-0 break-words text-ink">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Table({ className, children, ...rest }: HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="overflow-x-auto">
      <table
        className={['w-full border-collapse text-body', className].filter(Boolean).join(' ')}
        {...rest}
      >
        {children}
      </table>
    </div>
  );
}

/** A column head: quiet, on a tinted band so the head separates from the first row. */
export function Th({ className, children, ...rest }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      scope="col"
      className={[
        'bg-surface-2 text-left font-medium text-meta text-ink-3 px-3 py-2 border-b border-line whitespace-nowrap',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {children}
    </th>
  );
}

export function Td({ className, children, ...rest }: TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td
      className={['px-3 py-2 border-b border-line align-top', className].filter(Boolean).join(' ')}
      {...rest}
    >
      {children}
    </td>
  );
}

/** A section heading with an optional count beside it (outside the heading, so the name stays clean). */
export function SectionTitle({
  children,
  count,
  className,
}: {
  children: ReactNode;
  count?: number;
  className?: string;
}) {
  return (
    <div className={['flex items-baseline gap-2', className].filter(Boolean).join(' ')}>
      <h2 className="text-title font-medium">{children}</h2>
      {count !== undefined ? <span className="text-body text-ink-3 tabular">{count}</span> : null}
    </div>
  );
}
